import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Container } from "reactstrap";
import { ArrowLeft } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";
import ToggleSwitch from "../../shared/ToggleSwitch";
import MultiSelectField from "../common/MultiSelectField";
import { getApp, createApp, editApp, fetchMerchantsForApps } from "../../utils/appsCalls";
import { getBrands } from "../../utils/brandApi";

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: C.textMed, marginBottom: 6 };
const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  fontSize: 13,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  outline: "none",
  fontFamily: "inherit",
  color: C.text,
  background: C.white,
};

const EditApp = () => {
  const navigate = useNavigate();
  const { appId } = useParams();
  const isEdit = !!appId;

  // Brands are merchant-scoped, so the list offered here follows the merchant picked in the
  // header — the same idiom every other merchant-scoped page in the panel uses.
  const userType = localStorage.getItem("userType");
  const storedMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? storedMerchantId : reduxSelectedMerchant;
  const needsMerchant = !selectedMerchant || selectedMerchant === "all";

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  // Which merchant-set brandOptions currently reflects, so the prune below can tell
  // "loaded and genuinely empty" from "still fetching".
  const [brandsLoadedKey, setBrandsLoadedKey] = useState(null);
  const [form, setForm] = useState({
    reference: "",
    name: "",
    moyasarSource: "",
    iosBundleId: "",
    androidPackage: "",
    isActive: true,
    minSupportedVersion: "",
    latestVersion: "",
    iosStoreUrl: "",
    androidStoreUrl: "",
    updateMessage: "",
    merchantIds: [],
    brandIds: [],
    brandBasedBranches: false,
    brandBasedProducts: false,
    otpMerchantId: null,
  });

  useEffect(() => {
    (async () => {
      const merchants = await fetchMerchantsForApps();
      setMerchantOptions(merchants.map((m) => ({ value: m.id, label: m.name || m.reference })));

      if (isEdit) {
        const app = await getApp(appId);
        if (app) {
          setForm({
            reference: app.reference || "",
            name: app.name || "",
            moyasarSource: app.moyasarSource || "",
            iosBundleId: app.iosBundleId || "",
            androidPackage: app.androidPackage || "",
            isActive: !!app.isActive,
            minSupportedVersion: app.minSupportedVersion || "",
            latestVersion: app.latestVersion || "",
            iosStoreUrl: app.iosStoreUrl || "",
            androidStoreUrl: app.androidStoreUrl || "",
            updateMessage: app.updateMessage || "",
            merchantIds: (app.merchants || []).map((m) => m.id),
            brandIds: (app.brands || []).map((b) => b.id),
            brandBasedBranches: !!app.brandBasedBranches,
            brandBasedProducts: !!app.brandBasedProducts,
            otpMerchantId: app.otpMerchantId ?? null,
          });
        }
        setLoading(false);
      }
    })();
  }, [appId, isEdit]);

  // Which merchants' brands to offer. An app may serve several (Allowed Merchants), and its
  // brand set may legitimately span them — the server scopes the filter per request, using
  // only the brands of whichever merchant is being browsed.
  //
  // When the app lists no merchants there is nothing to derive from, so fall back to the
  // merchant chosen in the header. Without that fallback the field is simply empty and looks
  // broken, since no app currently has an allowed-merchant list.
  const brandMerchantIds = form.merchantIds.length
    ? form.merchantIds
    : needsMerchant
      ? []
      : [Number(selectedMerchant)];

  // Only active brands are offered: an inactive one is excluded from the filter server-side
  // anyway, so listing it would imply an effect it doesn't have.
  const brandMerchantKey = brandMerchantIds.join(",");
  useEffect(() => {
    (async () => {
      const ids = brandMerchantKey ? brandMerchantKey.split(",").map(Number) : [];
      if (!ids.length) {
        setBrandOptions([]);
        return;
      }
      const perMerchant = await Promise.all(
        ids.map((id) =>
          getBrands(id, { active: true }).then((brands) =>
            (brands || []).map((b) => ({ ...b, MerchantId: b.MerchantId ?? id }))
          )
        )
      );
      setBrandOptions(perMerchant.flat());
      setBrandsLoadedKey(brandMerchantKey);
    })();
    // Keyed on the joined ids so a new array identity with the same contents doesn't refetch.
  }, [brandMerchantKey]);

  // Drop any selected brand that is no longer on offer — i.e. its merchant was just removed
  // from Allowed Merchants. Without this the id stays in form.brandIds while its chip is
  // invisible, and the save fails server-side complaining about a brand the admin can't see.
  // Guarded on brandsLoadedKey so it never runs against a not-yet-loaded (empty) option list,
  // which would wipe the app's existing selection on open.
  useEffect(() => {
    if (brandsLoadedKey === null || brandsLoadedKey !== brandMerchantKey) return;
    const offered = new Set(brandOptions.map((b) => b.id));
    setForm((prev) => {
      const kept = prev.brandIds.filter((id) => offered.has(id));
      return kept.length === prev.brandIds.length ? prev : { ...prev, brandIds: kept };
    });
  }, [brandsLoadedKey, brandMerchantKey, brandOptions]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!form.reference.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, reference: form.reference.trim() };
      const result = isEdit ? await editApp(appId, payload) : await createApp(payload);
      if (result) navigate("/apps/list");
    } finally {
      setSaving(false);
    }
  };

  const selectedMerchants = merchantOptions.filter((o) => form.merchantIds.includes(o.value));

  // Qualify each brand with its merchant only when more than one is in play — otherwise the
  // suffix is noise repeated on every row.
  const showBrandMerchant = brandMerchantIds.length > 1;
  const merchantLabel = (id) =>
    merchantOptions.find((o) => o.value === Number(id))?.label || `merchant #${id}`;
  const brandSelectOptions = brandOptions.map((b) => ({
    value: b.id,
    label: showBrandMerchant ? `${b.name} — ${merchantLabel(b.MerchantId)}` : b.name,
  }));
  const selectedBrands = brandSelectOptions.filter((o) => form.brandIds.includes(o.value));
  const noBrandsAttached = form.brandIds.length === 0;

  return (
    <Fragment>
      <Breadcrumb title={isEdit ? "Edit App" : "Add App"} parent="Apps" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {loading ? (
            <RLoader />
          ) : (
            <RCard>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <div
                  onClick={() => navigate("/apps/list")}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background: C.white,
                  }}
                >
                  <ArrowLeft size={16} color={C.textMed} />
                </div>
                <h5 style={{ margin: 0, color: C.text, fontWeight: 700 }}>
                  {isEdit ? `Edit ${form.name || form.reference}` : "Add App"}
                </h5>
              </div>

              <div
                style={{
                  background: C.coralLight,
                  color: C.coralDark || C.coral,
                  padding: "10px 14px",
                  borderRadius: 8,
                  fontSize: 12,
                  marginBottom: 20,
                }}
              >
                Note: status and version fields are stored and managed here, but are not yet
                enforced by the app. The merchant list defines which merchants this app may serve.
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
                <div>
                  <label style={labelStyle}>Reference (slug) *</label>
                  <input
                    style={{ ...inputStyle, ...(isEdit ? { background: C.bg || "#f8fafc", color: C.textLight } : {}) }}
                    value={form.reference}
                    disabled={isEdit}
                    placeholder="e.g. camelstep"
                    onChange={(e) => set("reference", e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Name</label>
                  <input style={inputStyle} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Camel Step" />
                </div>
                <div>
                  <label style={labelStyle}>Moyasar Source</label>
                  <input style={inputStyle} value={form.moyasarSource} onChange={(e) => set("moyasarSource", e.target.value)} placeholder="CS Pickup App" />
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                    Exact <code>metadata.Source</code> this app sends to Moyasar. Used to recognize its webhooks and ignore other products sharing the Moyasar account.
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>iOS Bundle ID</label>
                  <input style={inputStyle} value={form.iosBundleId} onChange={(e) => set("iosBundleId", e.target.value)} placeholder="com.camelstep.app.pickup" />
                </div>
                <div>
                  <label style={labelStyle}>Android Package</label>
                  <input style={inputStyle} value={form.androidPackage} onChange={(e) => set("androidPackage", e.target.value)} placeholder="com.steppickupapp" />
                </div>
                <div>
                  <label style={labelStyle}>Min Supported Version</label>
                  <input style={inputStyle} value={form.minSupportedVersion} onChange={(e) => set("minSupportedVersion", e.target.value)} placeholder="1.0.7" />
                </div>
                <div>
                  <label style={labelStyle}>Latest Version</label>
                  <input style={inputStyle} value={form.latestVersion} onChange={(e) => set("latestVersion", e.target.value)} placeholder="1.0.7" />
                </div>
                <div>
                  <label style={labelStyle}>iOS Store URL</label>
                  <input style={inputStyle} value={form.iosStoreUrl} onChange={(e) => set("iosStoreUrl", e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Android Store URL</label>
                  <input style={inputStyle} value={form.androidStoreUrl} onChange={(e) => set("androidStoreUrl", e.target.value)} />
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <label style={labelStyle}>Update Message</label>
                <input style={inputStyle} value={form.updateMessage} onChange={(e) => set("updateMessage", e.target.value)} placeholder="Optional message shown on update prompts" />
              </div>

              <div style={{ marginTop: 18 }}>
                <label style={labelStyle}>Allowed Merchants</label>
                <MultiSelectField
                  options={merchantOptions}
                  value={selectedMerchants}
                  onChange={(vals) => set("merchantIds", (vals || []).map((v) => v.value))}
                  placeholder="Select merchants this app may serve..."
                />
              </div>

              <div style={{ marginTop: 18 }}>
                <label style={labelStyle}>OTP / Login SMS merchant</label>
                {/* Native single select — MultiSelectField hardcodes isMulti. Deliberately
                    offers ALL merchants, not just Allowed Merchants: a dedicated house/
                    billing merchant may own app-level SMS (owner decision). */}
                <select
                  style={inputStyle}
                  value={form.otpMerchantId ?? ""}
                  onChange={(e) => set("otpMerchantId", e.target.value === "" ? null : Number(e.target.value))}
                >
                  <option value="">Not configured</option>
                  {merchantOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                  App-level sign-in (login before a merchant is picked, e.g. the common app)
                  sends its OTP through this merchant's SMS settings, and that merchant is
                  billed for those messages. Can be any merchant — it does not have to be in
                  Allowed Merchants. While not configured, app-level sign-in is rejected;
                  the normal per-merchant login is unaffected.
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <label style={labelStyle}>Brands</label>
                <MultiSelectField
                  options={brandSelectOptions}
                  value={selectedBrands}
                  onChange={(vals) => set("brandIds", (vals || []).map((v) => v.value))}
                  placeholder={
                    !brandMerchantIds.length
                      ? "Pick a merchant above, or in the header..."
                      : brandSelectOptions.length
                        ? "Select the brands this app serves..."
                        : "No active brands for these merchants yet..."
                  }
                  isDisabled={!brandMerchantIds.length || brandSelectOptions.length === 0}
                />
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                  {!brandMerchantIds.length
                    ? "Brands belong to a merchant — add one to Allowed Merchants above, or select one in the header."
                    : form.merchantIds.length
                      ? "Active brands of this app's allowed merchants. On its own this changes nothing — it takes effect through the two switches below."
                      : `Active brands of ${merchantLabel(selectedMerchant)} (from the header, since this app lists no allowed merchants). On its own this changes nothing — it takes effect through the two switches below.`}
                </div>
              </div>

              <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                  Brand-based visibility
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>
                  Filtering is strict: anything not tagged with one of the brands above is
                  hidden. Both switches are safe to leave off — and while no brand is
                  attached they do nothing at all, so tag your data first, then switch them on.
                </div>

                {noBrandsAttached && (form.brandBasedBranches || form.brandBasedProducts) && (
                  <div
                    style={{
                      background: "#FEF3C7",
                      color: "#92400E",
                      padding: "10px 14px",
                      borderRadius: 8,
                      fontSize: 12,
                      marginBottom: 14,
                    }}
                  >
                    No brands are attached to this app, so these switches have no effect yet.
                    The app keeps showing everything until you attach at least one brand.
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <ToggleSwitch
                    checked={form.brandBasedBranches}
                    handleToggle={() => set("brandBasedBranches", !form.brandBasedBranches)}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Brand-based branches</div>
                    <div style={{ fontSize: 11, color: C.textLight }}>
                      Only show branches tagged with one of this app's brands. Tag branches on
                      the branch edit screen.
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <ToggleSwitch
                    checked={form.brandBasedProducts}
                    handleToggle={() => set("brandBasedProducts", !form.brandBasedProducts)}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Brand-based products</div>
                    <div style={{ fontSize: 11, color: C.textLight }}>
                      Only show products tagged with one of this app's brands. Product tags come
                      from the merchant system on each menu sync.
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Active</label>
                <ToggleSwitch checked={form.isActive} handleToggle={() => set("isActive", !form.isActive)} />
              </div>

              <div style={{ marginTop: 26, display: "flex", gap: 10 }}>
                <button
                  onClick={submit}
                  disabled={saving || !form.reference.trim()}
                  style={{
                    padding: "10px 22px",
                    borderRadius: 8,
                    border: "none",
                    background: C.coral,
                    color: C.white,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving || !form.reference.trim() ? 0.6 : 1,
                  }}
                >
                  {saving ? "Saving..." : isEdit ? "Save Changes" : "Create App"}
                </button>
                <button
                  onClick={() => navigate("/apps/list")}
                  style={{
                    padding: "10px 22px",
                    borderRadius: 8,
                    border: `1.5px solid ${C.border}`,
                    background: C.white,
                    color: C.textMed,
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </RCard>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default EditApp;
